import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  adminToolStrategies,
  adminStrategiesSuggest,
  adminStrategiesVocab,
  adminAutoTagAll,
  adminAutoTagAllStatus,
  adminUpdate,
} from "../lib/api";

// Admin "+ strategy" panel — LLM-suggested, admin-approved strategy/use-case
// tagging. Each tool's strategies drive the /best/<strategy>-tools landing
// pages. Nothing goes live until you save; pages index once >= min_tools.
export default function AdminStrategies() {
  const [tools, setTools] = useState([]);
  const [vocab, setVocab] = useState([]); // [string]
  const [counts, setCounts] = useState([]); // [{value,slug,count,indexable}]
  const [minTools, setMinTools] = useState(4);
  const [q, setQ] = useState("");
  const [missingOnly, setMissingOnly] = useState(true);
  const [stats, setStats] = useState({ total: 0, tagged: 0 });
  const [draft, setDraft] = useState({}); // slug -> [strategies] working set
  const [suggested, setSuggested] = useState({}); // slug -> [strategies]
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [autoTag, setAutoTag] = useState({ running: false });
  const autoPoll = useRef(null);

  const loadVocab = useCallback(async () => {
    try {
      const d = await adminStrategiesVocab();
      setCounts(d.strategies || []);
      setMinTools(d.min_tools || 4);
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  const load = useCallback(async (query = q, only = missingOnly) => {
    setLoading(true);
    try {
      const d = await adminToolStrategies(query, only);
      setTools(d.tools || []);
      setVocab(d.vocabulary || []);
      setStats({ total: d.total || 0, tagged: d.tagged || 0 });
      const dr = {};
      (d.tools || []).forEach((t) => (dr[t.slug] = [...(t.strategies || [])]));
      setDraft(dr);
    } catch (e) {
      toast.error("could not load tool strategies");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    loadVocab();
  }, [load, loadVocab]);

  const toggle = (slug, strat) => {
    setDraft((d) => {
      const cur = d[slug] || [];
      if (cur.includes(strat)) {
        return { ...d, [slug]: cur.filter((s) => s !== strat) };
      }
      if (cur.length >= 6) {
        toast.error("max 6 strategies per tool");
        return d;
      }
      return { ...d, [slug]: [...cur, strat] };
    });
  };

  const dirty = (t) => {
    const a = [...(draft[t.slug] || [])].sort().join("|");
    const b = [...(t.strategies || [])].sort().join("|");
    return a !== b;
  };

  const dirtyCount = tools.filter(dirty).length;

  const save = async (t) => {
    try {
      const res = await adminUpdate(t.id, { strategies: draft[t.slug] || [] });
      const saved = res.strategies || [];
      setTools((ts) => ts.map((x) => (x.id === t.id ? { ...x, strategies: saved } : x)));
      setDraft((d) => ({ ...d, [t.slug]: [...saved] }));
      toast.success(`saved — ${t.title}`);
      loadVocab();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save");
    }
  };

  const saveAll = async () => {
    const pending = tools.filter(dirty);
    if (!pending.length) return;
    setSavingAll(true);
    let ok = 0;
    for (const t of pending) {
      try {
        const res = await adminUpdate(t.id, { strategies: draft[t.slug] || [] });
        const saved = res.strategies || [];
        setTools((ts) => ts.map((x) => (x.id === t.id ? { ...x, strategies: saved } : x)));
        setDraft((d) => ({ ...d, [t.slug]: [...saved] }));
        ok += 1;
      } catch (e) {
        /* keep going; report total at end */
      }
    }
    setSavingAll(false);
    toast.success(`saved ${ok}/${pending.length} tool${pending.length === 1 ? "" : "s"}`);
    loadVocab();
  };

  const suggestFor = async (slugs) => {
    setSuggesting(true);
    try {
      const d = await adminStrategiesSuggest(
        slugs ? { slugs } : { onlyMissing: missingOnly, limit: 12 },
      );
      const sug = d.suggestions || {};
      setSuggested((s) => ({ ...s, ...sug }));
      // Auto-fill the AI picks straight into the editable draft so the chips
      // light up and the rows become "unsaved" — the admin just reviews and
      // clicks Save (no hidden "apply" step).
      setDraft((dr) => {
        const next = { ...dr };
        for (const [slug, vals] of Object.entries(sug)) {
          next[slug] = [...new Set([...(next[slug] || []), ...vals])].slice(0, 6);
        }
        return next;
      });
      const withPicks = Object.values(sug).filter((v) => v.length).length;
      const n = Object.keys(sug).length;
      toast.success(
        `AI filled ${withPicks} of ${n} tool${n === 1 ? "" : "s"} — review the chips, then Save`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const pollAutoTag = () => {
    if (autoPoll.current) clearInterval(autoPoll.current);
    autoPoll.current = setInterval(async () => {
      try {
        const s = await adminAutoTagAllStatus();
        setAutoTag(s);
        if (!s.running) {
          clearInterval(autoPoll.current);
          autoPoll.current = null;
          toast.success(`auto-tagged ${s.tagged}/${s.total} tools — pages updating`);
          load(q, missingOnly);
          loadVocab();
        }
      } catch (e) {
        /* ignore */
      }
    }, 3000);
  };

  const autoTagAll = async () => {
    try {
      setAutoTag({ running: true, total: 0, done: 0, tagged: 0 });
      const r = await adminAutoTagAll();
      if (r.already_running) toast.info("auto-tagging already in progress");
      else toast.success("auto-tagging your catalog with AI…");
      pollAutoTag();
    } catch (e) {
      setAutoTag({ running: false });
      toast.error(e?.response?.data?.detail || "could not start auto-tag");
    }
  };

  useEffect(() => () => autoPoll.current && clearInterval(autoPoll.current), []);

  const liveCount = counts.filter((c) => c.indexable).length;

  return (
    <div className="space-y-4" data-testid="admin-strategies">
      <div className="text-[13px] text-[#444] leading-relaxed">
        <span className="font-bold">Strategy / use-case tagging — automatic.</span> New tools are
        AI-tagged the moment you approve them, and their{" "}
        <span className="font-mono">/best/&lt;strategy&gt;-tools</span> pages publish themselves
        (≥{minTools} tools). No approval needed. Use{" "}
        <span className="font-bold">✦ auto-tag all untagged</span> to sweep your existing catalog,
        and the chips below only if you want to correct or fine-tune a tool.
      </div>

      {/* Live strategy pages summary */}
      <div className="border border-[#eee] p-2" data-testid="strategy-vocab">
        <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">
          strategy pages · {liveCount} live / {counts.length}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {counts.map((c) => (
            <span
              key={c.slug}
              className={`text-[11px] px-1.5 py-0.5 border ${
                c.indexable ? "border-black bg-black text-white" : "border-[#ccc] text-[#888]"
              }`}
              title={c.indexable ? `live: /best/${c.slug}` : `needs ${minTools - c.count} more`}
              data-testid={`strategy-count-${c.slug}`}
            >
              {c.value} {c.count}
            </span>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="tv-input text-[13px] flex-1 min-w-[160px]"
          placeholder="search tools by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q, missingOnly)}
          data-testid="strategy-search"
        />
        <label className="text-[12px] flex items-center gap-1">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => {
              setMissingOnly(e.target.checked);
              load(q, e.target.checked);
            }}
            data-testid="strategy-missing-only"
          />
          untagged only
        </label>
        <button className="tv-btn tv-btn-primary" onClick={() => load(q, missingOnly)} data-testid="strategy-load">
          [load]
        </button>
        <button
          className="tv-btn tv-btn-primary"
          disabled={autoTag.running}
          onClick={autoTagAll}
          title="AI-tag every untagged tool and save directly (no review)"
          data-testid="strategy-autotag-all"
        >
          {autoTag.running
            ? `auto-tagging${autoTag.total ? ` ${autoTag.total}` : ""}…`
            : "[✦ auto-tag all untagged]"}
        </button>
        <button
          className="tv-btn"
          disabled={suggesting}
          onClick={() => suggestFor(null)}
          title="Fill chips for the untagged tools so you can review before saving"
          data-testid="strategy-suggest-all"
        >
          {suggesting ? "suggesting…" : "[✦ suggest (review)]"}
        </button>
        <button
          className="tv-btn"
          disabled={savingAll || dirtyCount === 0}
          onClick={saveAll}
          title="Save every row with unsaved changes"
          data-testid="strategy-save-all"
        >
          {savingAll ? "saving…" : `[save all${dirtyCount ? ` (${dirtyCount})` : ""}]`}
        </button>
        <span className="text-[12px] text-[#888]" data-testid="strategy-stats">
          {stats.tagged} tagged · {stats.total} {missingOnly ? "untagged" : "shown"}
          {dirtyCount > 0 ? ` · ${dirtyCount} unsaved` : ""}
        </span>
      </div>

      {/* Tool list */}
      {loading ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="strategy-loading">loading…</div>
      ) : tools.length === 0 ? (
        <div className="text-[13px] text-[#888] py-4" data-testid="strategy-empty">
          {missingOnly ? "every tool here is tagged 🎉" : "no tools."}
        </div>
      ) : (
        <div className="border border-[#eee] divide-y divide-[#eee]" data-testid="strategy-list">
          {tools.map((t) => {
            const sel = draft[t.slug] || [];
            const sug = suggested[t.slug];
            return (
              <div key={t.id} className="p-2.5" data-testid={`strategy-row-${t.slug}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[13px] font-bold">
                    {t.title}
                    <span className="text-[11px] text-[#999] font-normal ml-2">
                      {[t.category, t.asset_class].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      className="tv-btn text-[11px]"
                      disabled={suggesting}
                      onClick={() => suggestFor([t.slug])}
                      data-testid={`strategy-suggest-${t.slug}`}
                    >
                      ✦ suggest
                    </button>
                    <button
                      className="tv-btn tv-btn-primary text-[11px]"
                      disabled={!dirty(t)}
                      onClick={() => save(t)}
                      data-testid={`strategy-save-${t.slug}`}
                    >
                      {dirty(t) ? "save" : "saved"}
                    </button>
                  </div>
                </div>

                {/* AI provenance marker — chips below already reflect the picks */}
                {sug !== undefined && (
                  <div className="mt-1.5 text-[11px] text-[#777]" data-testid={`strategy-suggestion-${t.slug}`}>
                    {sug.length
                      ? "✦ AI-filled the chips below — review & Save"
                      : "✦ AI found no clear strategy match"}
                  </div>
                )}

                {/* Vocab chips (click to toggle) */}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {vocab.map((v) => {
                    const on = sel.includes(v);
                    return (
                      <button
                        key={v}
                        onClick={() => toggle(t.slug, v)}
                        className={`text-[11px] px-1.5 py-0.5 border ${
                          on ? "border-black bg-black text-white" : "border-[#ccc] text-[#666] hover:border-black"
                        }`}
                        data-testid={`strategy-chip-${t.slug}-${v.replace(/\W+/g, "-").toLowerCase()}`}
                      >
                        {on ? "✓ " : ""}{v}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
